/**
 * The "password setup pending" cell of the tenant user table.
 *
 * WHAT CHANGED (6-7 Sep 2026). The list used to carry `password_reset_token`
 * for every ever-ready tenant so this cell could assemble the reset link in
 * the browser. A credential in a read response is a leak whatever the button
 * does with it — `tenant:read` alone could fetch it. The list now carries only
 * the FACT (`password_setup_pending`); the link is issued on demand by
 * `POST /users/{id}/password-setup-link`, which the backend gates on
 * `user:reset_password` and audits. An operator without that permission sees
 * the pending state and a refusal, never a link.
 */
import React, { useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Key, Loader2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const SETUP_LINK_URL = (userId) => `${BACKEND_URL}/api/admin/users/${userId}/password-setup-link`;

/**
 * The absolute link an operator can hand to a person — THE SERVER'S, not ours.
 *
 * This used to be `${window.location.origin}${resetPath}`, and that origin is
 * the ADMIN PORTAL. The copied link was
 * `https://admin.cluvik.com/reset-password?token=...`, a route the Admin SPA
 * does not have — verified live, it answers 200 with the Admin application.
 * So every operator-issued setup link was broken in every environment, and it
 * carried a live token to an origin that could not use it.
 *
 * The server now resolves the host from the tenant's own environment
 * (`reset_url`). Returns null when it did not, so the caller refuses rather
 * than pasting something that cannot work.
 */
export function resetLinkFor(data) {
  return (data && data.reset_url) || null;
}

/** The operator-facing message for a refusal, from the server's own vocabulary. */
export function describeRefusal(status, detail) {
  const code = detail && typeof detail === 'object' ? detail.error : null;
  if (status === 403) return 'You do not hold user:reset_password, so a setup link cannot be issued to you.';
  if (code === 'password_setup_withheld') return 'This tenant has not completed provisioning; repair it before releasing access.';
  if (code === 'no_active_setup_token') return 'No live setup token for this user — issue a temporary password instead.';
  if (detail && typeof detail === 'object' && detail.message) return detail.message;
  return 'The setup link could not be issued.';
}

export default function PasswordSetupCell({ user, headers }) {
  const [busy, setBusy] = useState(false);

  const issue = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(SETUP_LINK_URL(user.id), {}, { headers });
      const link = resetLinkFor(data);
      if (!link) {
        toast.error('The server did not say which environment this link belongs to; nothing was copied.');
        return;
      }
      await navigator.clipboard.writeText(link);
      toast.success('Setup link copied to clipboard');
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      toast.error(describeRefusal(status, detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      {/* Waiting on the person, not a fault: the in-progress tone, through the
          token system, not a raw amber swatch that neither moves with the
          theme nor teaches an operator anything about amber. */}
      <Badge variant="info">Pending</Badge>
      <Button
        size="sm"
        variant="outline"
        className="h-7 text-xs"
        onClick={issue}
        disabled={busy}
        aria-label={`Copy password setup link for ${user.email || user.id}`}
        data-testid={`copy-reset-link-${user.id}`}
      >
        {busy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Key className="h-3 w-3 mr-1" />}
        Copy Reset Link
      </Button>
    </div>
  );
}
